import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Link,
  Paper,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTwitter,
  faInstagram,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import {
  faHeart,
  faLock,
  faHandsHelping,
  faInfoCircle,
  faBullhorn,
  faEnvelope,
  faPlus,
  faLightbulb,
  faMoneyBillWave,
  faQuestionCircle,
  faUserPlus,
  faRightToBracket,
} from '@fortawesome/free-solid-svg-icons';

import COLORS from '../../constants/colors';
import SideNav from './Sidenav';
import Bars_icon_light from '../../assets/icons/side.svg';
import logo from '../../assets/logo.png';

// Import your new reusable button components
import { PrimaryButton, OutlinedButton } from '../Reusable/Button';

// --- NEW COMPONENT: SubMenuItem (No change needed here, it already has the hover effect) ---
const SubMenuItem = ({ label, href, icon, description }) => (
  <Link
    href={href}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      color: 'inherit',
      textDecoration: 'none',
      padding: '12px 15px',
      borderRadius: '8px',
      transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#f5f5f5', // This is the hover background
        color: COLORS.primary,
        '& .MuiTypography-root': {
          color: COLORS.primary,
        },
        '& .svg-inline--fa': {
          // Target the FontAwesome SVG directly
          color: COLORS.primary,
        },
      },
    }}
  >
    <FontAwesomeIcon icon={icon} style={{ fontSize: '20px', color: COLORS.primary }} />
    <Box>
      <Typography fontWeight={600} fontSize={16} sx={{ color: 'text.primary' }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, mt: 0.5 }}>
        {description}
      </Typography>
    </Box>
  </Link>
);

// --- MAIN HEADER COMPONENT ---
const Header = ({ mode }) => {
  const [hoverMenu, setHoverMenu] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const timeoutRef = useRef(null); // Use a ref to store the timeout ID

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
    setHoverMenu(null);
  };

  const handleMouseEnter = (menu) => {
    clearTimeout(timeoutRef.current); // Clear any existing timeout
    setHoverMenu(menu);
  };

  const handleMouseLeave = () => {
    // Set a timeout to close the menu after a brief delay
    timeoutRef.current = setTimeout(() => {
      setHoverMenu(null);
    }, 200); // 200ms delay
  };

  const menuLinkStyle = {
    color: COLORS.primary,
    fontWeight: 600,
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textTransform: 'none',
    textDecoration: 'none',
    position: 'relative',
    margin: '0 20px',
    padding: '10px 0',
    userSelect: 'none',
    transition: 'color 0.3s ease-in-out, background-color 0.3s ease-in-out',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: COLORS.lightGray,
      color: COLORS.primary,
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '3px',
      backgroundColor: COLORS.primary,
      bottom: '-6px',
      left: 0,
      transform: 'scaleX(0)',
      transformOrigin: 'bottom right',
      transition: 'transform 0.4s ease-out',
    },
    '&:hover:after': {
      transform: 'scaleX(1)',
      transformOrigin: 'bottom left',
    },
  };

  const submenuItemsData = {
    donate: [
      {
        label: 'Campaigns',
        href: '/campaigns',
        icon: faBullhorn,
        description: 'Discover impactful campaigns you can support to make a difference.',
      },
      {
        label: 'Login',
        href: '/user/login',
        icon: faLock,
        description: 'Login to your account, create and manage your campaigns',
      },
      {
        label: 'Impact',
        href: '/impact',
        icon: faHandsHelping,
        description: 'See the real-world change made possible by generous donations.',
      },
      {
        label: 'How it Works',
        href: '/how-it-works',
        icon: faInfoCircle,
        description: 'Learn how our platform facilitates donations effectively and safely.',
      },
      {
        label: 'Contact Us',
        href: '/Contact-us',
        icon: faEnvelope,
        description: 'Get in touch with our support team for any questions or help.',
      },
      
    ],
    fundraise: [
      {
        label: 'Start a Campaign',
        href: '/user/register',
        icon: faHeart,
        description: 'Lets create your account and you can create your own fundraising campaign and share your story.',
      },
      {
        label: 'Fundraising Tips',
        href: '/fundraising-tips',
        icon: faInfoCircle,
        description: 'Helpful advice to maximize the success of your fundraiser.',
      },
    ],


    organization: [
      {
        label: 'Create Account',
        href: '/org/register',
        icon: faUserPlus,
        description: 'Create an organization account for your NGO or Community based organisation and create and manage your cmapaigns effeciently',
      },
      {
        label: 'Login',
        href: '/user/login',
        icon: faLock,
        description: 'Have an organization account, Login from here',
      },
    ],
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingY: { xs: 1.5, md: 2 },
          paddingX: { xs: 2, sm: 4, md: 8 },
          userSelect: 'none',
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="Yamba Logo" style={{ height: 40, width: 'auto', marginRight: 12 }} />
        </Box>

        {/* Main Navigation - desktop */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            flexGrow: 1,
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {/* Home */}
          <Link href="/" sx={menuLinkStyle}>
            Home
          </Link>

          {/* Donate menu */}
          <Box
            onMouseEnter={() => handleMouseEnter('donate')}
            onMouseLeave={handleMouseLeave}
            sx={{ ...menuLinkStyle, position: 'relative' }}
          >
            Donate
            {/* Mega submenu */}
            {hoverMenu === 'donate' && (
              <Paper
                elevation={6}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 15px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'background.paper',
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1,
                  padding: 2,
                  borderRadius: '12px',
                  minWidth: '350px',
                  maxWidth: '500px',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.2), 0 5px 10px rgba(0,0,0,0.1)',
                  zIndex: 1200,
                  userSelect: 'auto',
                  transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
                }}
              >
                {submenuItemsData.donate.map((item) => (
                  <SubMenuItem key={item.label} {...item} />
                ))}
              </Paper>
            )}
          </Box>

          {/* Start Fundraise menu */}
          <Box
            onMouseEnter={() => handleMouseEnter('fundraise')}
            onMouseLeave={handleMouseLeave}
            sx={{ ...menuLinkStyle, position: 'relative' }}
          >
            Start Fundraise
            {/* Mega submenu */}
            {hoverMenu === 'fundraise' && (
              <Paper
                elevation={6}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 15px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'background.paper',
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1,
                  padding: 2,
                  borderRadius: '12px',
                  minWidth: '350px',
                  maxWidth: '500px',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.2), 0 5px 10px rgba(0,0,0,0.1)',
                  zIndex: 1200,
                  userSelect: 'auto',
                  transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
                }}
              >
                {submenuItemsData.fundraise.map((item) => (
                  <SubMenuItem key={item.label} {...item} />
                ))}
              </Paper>
            )}
          </Box>


          {/* Donate menu */}
          <Box
            onMouseEnter={() => handleMouseEnter('organizations')}
            onMouseLeave={handleMouseLeave}
            sx={{ ...menuLinkStyle, position: 'relative' }}
          >
            Organizations
            {/* Mega submenu */}
            {hoverMenu === 'organizations' && (
              <Paper
                elevation={6}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 15px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'background.paper',
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1,
                  padding: 2,
                  borderRadius: '12px',
                  minWidth: '350px',
                  maxWidth: '500px',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.2), 0 5px 10px rgba(0,0,0,0.1)',
                  zIndex: 1200,
                  userSelect: 'auto',
                  transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
                }}
              >
                {submenuItemsData.organization.map((item) => (
                  <SubMenuItem key={item.label} {...item} />
                ))}
              </Paper>
            )}
          </Box>
        </Box>

        

        {/* Right side: Register, Login, Socials, Hamburger */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1 } }}>
          {/* Register Button (using OutlinedButton) */}
          <OutlinedButton // <--- REPLACED Button with OutlinedButton
            component="a"
            href="/user/register"
          >
            Register
          </OutlinedButton>

          {/* Login Button (using PrimaryButton) */}
          <PrimaryButton // <--- REPLACED Button with PrimaryButton
            component="a"
            href="/user/login"
          >
            Login
          </PrimaryButton>

          {/* Socials */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, ml: { xs: 1, md: 2 }, gap: { xs: 0.5, md: 1 } }}>
            <IconButton
              component="a"
              href="https://www.linkedin.com/company/yamba-africa"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: COLORS.primary }}
              size="small"
            >
              <FontAwesomeIcon icon={faLinkedin} />
            </IconButton>

            <IconButton
              component="a"
              href="https://x.com/yamba_africa"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: COLORS.primary }}
              size="small"
            >
              <FontAwesomeIcon icon={faTwitter} />
            </IconButton>

            <IconButton
              component="a"
              href="https://www.instagram.com/yamba_africa/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: COLORS.primary }}
              size="small"
            >
              <FontAwesomeIcon icon={faInstagram} />
            </IconButton>
          </Box>

          {/* Hamburger for mobile */}
          <IconButton
            onClick={toggleDrawer}
            sx={{ display: { xs: 'block', lg: 'none' }, ml: { xs: 0.5, md: 1 } }}
            size="large"
          >
            <img src={Bars_icon_light} alt="Menu Icon" style={{ width: 28, height: 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile side nav */}
      <SideNav drawerOpen={drawerOpen} toggleDrawer={toggleDrawer} mode={mode} />
    </>
  );
};

export default Header;